import { useState, useEffect, useCallback } from 'react'
import { Experiment } from '../types'
import { supabase } from '../supabaseClient'

const DEFAULT_TAXONOMY = {
  framework: 'CDT',
  types: ['fact', 'concept', 'procedure', 'process', 'principle', 'unclear'],
}

const DEFAULT_MATERIAL_LEVELS = [
  { id: 'pass1', label: 'Requirement', hasGenerate: false },
  { id: 'pass2', label: 'Content', hasGenerate: true },
  { id: 'pass3', label: 'Knowledge', hasGenerate: true },
]

/**
 * Manages experiments in Supabase.
 * Standards are stored in experiment_standards junction table and hydrated on load.
 */
export function useExperiments(userId: string) {
  const [experiments, setExperiments] = useState<Experiment[]>([])
  const [loading, setLoading] = useState(true)

  // ── Load experiments with hydrated standards ─────────────────────────────
  const load = useCallback(async () => {
    if (!userId) {
      setExperiments([])
      setLoading(false)
      return
    }

    const { data: exps, error } = await supabase
      .from('experiments')
      .select('*, experiment_standards(standard_id, grade)')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('useExperiments load error:', error)
      setLoading(false)
      return
    }

    console.log('[load] raw fetch result', {
      userId,
      count: (exps ?? []).length,
      rows: (exps ?? []).map((r: Record<string, unknown>) => ({
        id: r.id,
        user_id: r.user_id,
        framework_definitions_length: ((r.framework_definitions as string) || '').length,
        framework_preview: ((r.framework_definitions as string) || '').slice(0, 40),
        updated_at: r.updated_at,
      })),
    })

    const hydrated: Experiment[] = (exps ?? []).map((row: Record<string, unknown>) => {
      const junctionRows = (row.experiment_standards as { standard_id: string; grade: string }[]) ?? []
      const standards = junctionRows.map((r) => r.standard_id)
      const grades = Array.from(new Set(junctionRows.map((r) => r.grade).filter(Boolean))).sort() as string[]

      return {
        id: row.id as string,
        user_id: row.user_id as string,
        name: row.name as string,
        research_question: row.research_question as string | null,
        hypothesis: row.hypothesis as string | null,
        methodology_notes: row.methodology_notes as string | null,
        dataset_description: row.dataset_description as string | null,
        // Previously dropped — these three columns come back from the
        // REST fetch but weren't being copied into the hydrated object,
        // so framework/system_prompt appeared empty in the UI even
        // though the DB held the real values.
        conditions_description: (row.conditions_description as string | null) ?? null,
        framework_definitions: (row.framework_definitions as string | null) ?? null,
        system_prompt: (row.system_prompt as string | null) ?? null,
        subject: row.subject,
        taxonomy: row.taxonomy ?? DEFAULT_TAXONOMY,
        material_levels: row.material_levels ?? DEFAULT_MATERIAL_LEVELS,
        sampling_strategy: row.sampling_strategy ?? { method: 'handpick' },
        evaluation_dimensions: row.evaluation_dimensions ?? [],
        analysis_metrics: row.analysis_metrics ?? ['coverage_rate', 'type_distribution'],
        phase: row.phase as string,
        status: row.status as string,
        version: row.version as number,
        started_at: (row.started_at as string | null) ?? null,
        copy_source_experiment_id: (row.copy_source_experiment_id as string | null) ?? null,
        audit_mode: (row.audit_mode as string) || 'full',
        created_at: row.created_at as string,
        updated_at: row.updated_at as string,
        standards,
        grades,
      } as Experiment
    })

    setExperiments(hydrated)
    setLoading(false)
  }, [userId])

  useEffect(() => {
    load()
  }, [load])

  // ── Create experiment ────────────────────────────────────────────────────
  const createExperiment = useCallback(
    async (draft: {
      name: string
      research_question?: string
      subject: string
      standards: string[]
      grades: string[]
      sampling_strategy?: Experiment['sampling_strategy']
      taxonomy?: Experiment['taxonomy']
      material_levels?: Experiment['material_levels']
      evaluation_dimensions?: Experiment['evaluation_dimensions']
      analysis_metrics?: Experiment['analysis_metrics']
    }): Promise<Experiment | null> => {
      // Insert experiment row
      const { data: exp, error } = await supabase
        .from('experiments')
        .insert({
          user_id: userId,
          name: draft.name,
          research_question: draft.research_question ?? null,
          subject: draft.subject,
          taxonomy: draft.taxonomy ?? DEFAULT_TAXONOMY,
          material_levels: draft.material_levels ?? DEFAULT_MATERIAL_LEVELS,
          sampling_strategy: draft.sampling_strategy ?? { method: 'handpick' },
          evaluation_dimensions: draft.evaluation_dimensions ?? [],
          analysis_metrics: draft.analysis_metrics ?? ['coverage_rate', 'type_distribution'],
        })
        .select()
        .single()

      if (error || !exp) {
        console.error('useExperiments create error:', error)
        return null
      }

      // Insert experiment_standards junction rows
      if (draft.standards.length > 0) {
        const rows = draft.standards.map((stdId) => ({
          experiment_id: exp.id,
          standard_id: stdId,
          grade: draft.grades.find(() => true) ?? null, // will be set per-standard below
        }))

        // Try to match each standard to its grade from the grades array
        // For now, use the grade lookup from StandardsProvider at the call site
        const { error: junctionErr } = await supabase.from('experiment_standards').insert(rows)
        if (junctionErr) console.error('useExperiments junction insert error:', junctionErr)
      }

      await load()

      // Return the newly created experiment
      const created = experiments.find((e) => e.id === exp.id)
      if (created) return created

      // If not yet in state (race), re-fetch
      const { data: fresh } = await supabase
        .from('experiments')
        .select('*, experiment_standards(standard_id, grade)')
        .eq('id', exp.id)
        .single()

      if (!fresh) return null

      const junctionRows = (fresh.experiment_standards as { standard_id: string; grade: string }[]) ?? []
      return {
        ...fresh,
        standards: junctionRows.map((r: { standard_id: string }) => r.standard_id),
        grades: Array.from(new Set(junctionRows.map((r: { grade: string }) => r.grade).filter(Boolean))).sort(),
      } as Experiment
    },
    [userId, load, experiments],
  )

  // ── Delete experiment ────────────────────────────────────────────────────
  const deleteExperiment = useCallback(
    async (id: string) => {
      const { error } = await supabase.from('experiments').delete().eq('id', id)
      if (error) console.error('useExperiments delete error:', error)
      await load()
    },
    [load],
  )

  // ── Update experiment fields ─────────────────────────────────────────────
  // Throws on error so callers (Save buttons, autosave) can surface real
  // failures instead of silently flipping UI to "saved" on a dropped write.
  const updateExperiment = useCallback(
    async (id: string, patch: Partial<Omit<Experiment, 'id' | 'user_id' | 'standards' | 'grades'>>) => {
      const { data, error } = await supabase.from('experiments').update(patch).eq('id', id).select()
      if (error) {
        console.error('useExperiments update error:', error)
        throw new Error(error.message)
      }
      if (!data || data.length === 0) {
        // update matched zero rows — usually auth/RLS mismatch. Would otherwise
        // succeed silently with no effect.
        throw new Error(
          `Update matched 0 rows for experiment ${id}. Likely an auth/RLS mismatch — check you're signed in as the owner.`,
        )
      }
      await load()
    },
    [load],
  )

  // ── Begin experiment: wipes pilot KUs + runs, transitions to collection ──
  const beginExperiment = useCallback(
    async (id: string): Promise<{ ok: boolean; error?: string }> => {
      const { error } = await supabase.rpc('begin_experiment', { exp_id: id })
      if (error) {
        console.error('useExperiments begin error:', error)
        return { ok: false, error: error.message }
      }
      await load()
      return { ok: true }
    },
    [load],
  )

  // ── Update experiment standards ──────────────────────────────────────────
  const setExperimentStandards = useCallback(
    async (experimentId: string, standards: { standard_id: string; grade: string }[]) => {
      // Delete existing junction rows and re-insert
      await supabase.from('experiment_standards').delete().eq('experiment_id', experimentId)

      if (standards.length > 0) {
        const rows = standards.map((s) => ({
          experiment_id: experimentId,
          standard_id: s.standard_id,
          grade: s.grade,
        }))
        const { error } = await supabase.from('experiment_standards').insert(rows)
        if (error) console.error('useExperiments setStandards error:', error)
      }

      await load()
    },
    [load],
  )

  return {
    experiments,
    loading,
    createExperiment,
    deleteExperiment,
    updateExperiment,
    setExperimentStandards,
    beginExperiment,
    reload: load,
  }
}
